import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Check, Edit2, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { getPlans } from '../../services/storage';
import type { Plan } from '../../types';

export const PlansAdmin = () => {
  const [plans] = useState<Plan[]>(getPlans());

  return (
    <DashboardLayout>
      <Helmet>
        <title>Planos | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Planos</h1>
            <p className="text-neutral-600">Gerencie os planos disponíveis</p>
          </div>
          <Button variant="primary" className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Novo Plano
          </Button>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className={`p-6 ${plan.isPopular ? 'border-2 border-primary-blue' : ''}`}>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-neutral-900">{plan.name}</h3>
                  <p className="text-sm text-neutral-600">{plan.description}</p>
                </div>
                {plan.isPopular && (
                  <Badge variant="primary">Popular</Badge>
                )}
              </div>

              <div className="mb-6">
                <div className="flex items-baseline">
                  <span className="text-3xl font-bold text-neutral-900">
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                    }).format(plan.monthlyPrice)}
                  </span>
                  <span className="text-neutral-500 ml-1">/mês</span>
                </div>
                <p className="text-sm text-neutral-500 mt-1">
                  {new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(plan.yearlyPrice)}/ano (economize 20%)
                </p>
              </div>

              <div className="space-y-2 mb-6">
                <div className="flex items-center gap-2 text-sm text-neutral-600">
                  <Check className="w-4 h-4 text-success flex-shrink-0" />
                  <span>Até {plan.maxUsers === 999999 ? 'usuários ilimitados' : `${plan.maxUsers} usuários`}</span>
                </div>
                {plan.features.map((feature, idx) => (
                  <div key={idx} className="flex items-center gap-2 text-sm text-neutral-600">
                    <Check className="w-4 h-4 text-success flex-shrink-0" />
                    <span>{feature}</span>
                  </div>
                ))}
              </div>

              <div className="flex gap-2">
                <Button variant="outline" className="flex-1 flex items-center justify-center gap-2">
                  <Edit2 className="w-4 h-4" />
                  Editar
                </Button>
                <Button variant="outline" className="px-3 text-red-600 hover:bg-red-50">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};
